import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown } from "lucide-react";

const ax = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`,
  withCredentials: true,
});

// Soft portfolio safeguard: shows a compact, expandable warning when selected
// recommendations share findings. Never blocks, never changes numbers.
export function OverlapWarning({ engagementId, scope, title, description, refreshKey }) {
  const [pairs, setPairs] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    ax.get(`/engagements/${engagementId}/recommendation-overlaps`)
      .then((r) => { if (alive) setPairs((r.data && r.data[scope]) || []); })
      .catch(() => { if (alive) setPairs([]); });
    return () => { alive = false; };
  }, [engagementId, scope, refreshKey]);

  if (!pairs.length) return null;

  return (
    <div
      className="border border-[#FDE68A] bg-[#FEFCE8] rounded-sm px-3 py-2.5"
      data-testid={`overlap-warning-${scope}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
        data-testid={`overlap-toggle-${scope}`}
      >
        <AlertTriangle className="w-4 h-4 text-[#A16207] shrink-0" />
        <span className="text-[13px] font-semibold text-[#92400E]">
          {title || "Overlapping Recommendation Coverage"}
        </span>
        <span className="ml-auto text-[11px] font-medium text-[#A16207]">
          {pairs.length} overlap{pairs.length > 1 ? "s" : ""}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#A16207] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-[11px] text-[#92400E] mt-1 leading-snug">
        {description ||
          "Some selected recommendations address the same findings. Review the selected recommendations to avoid overlapping implementation scope and ROI benefits."}
      </p>
      {open ? (
        <ul className="mt-2 space-y-1.5">
          {pairs.map((p, i) => (
            <li
              key={`${p.a.id}-${p.b.id}`}
              className="text-[11px] text-[#78350F] bg-white/70 border border-[#FDE68A] rounded-sm px-2 py-1"
              data-testid={`overlap-pair-${scope}-${i}`}
            >
              <span className="font-semibold">{p.a.rec_code}</span>
              {" and "}
              <span className="font-semibold">{p.b.rec_code}</span>
              {" both address: "}
              <span className="font-medium">{(p.finding_codes || []).join(", ")}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
